import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import './info-hint.component.scss';

/* Shared ⓘ field hint for the Employer Certificate sections.

   The bubble is portaled to <body> and positioned with `position: fixed`
   from the trigger's bounding rect, for the same reason the datepicker
   popper in employment-details is portaled: every card lives inside
   `.employer-certificate-order-form`, which is `overflow: hidden`, and the
   cards themselves clip too. An absolutely positioned bubble was therefore
   cut off at the nearest clipping edge — very visible on phones, where the
   240px bubble is wider than the space left of / right of the icon.

   Out of the tree, the only thing that can clip it is the viewport, and the
   placement below clamps against that: it flips top ↔ bottom when the
   preferred side has no room, clamps horizontally into the viewport, and
   slides the arrow so it keeps pointing at the icon after clamping. */

const GUTTER = 8;       // smallest allowed gap to a viewport edge
const OFFSET = 8;       // gap between the icon and the bubble
const ARROW_INSET = 12; // keeps the arrow clear of the rounded corners

type Placement = 'top' | 'bottom';

interface InfoHintProps {
    text: string;
    /** Preferred side. Flips to the other one when there is no room. */
    placement?: Placement;
}

interface BubblePosition {
    top: number;
    left: number;
    arrowLeft: number;
    placement: Placement;
}

export const InfoHint: React.FC<InfoHintProps> = ({ text, placement = 'top' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState<BubblePosition | null>(null);
    const triggerRef = useRef<HTMLSpanElement>(null);
    const bubbleRef = useRef<HTMLSpanElement>(null);
    /* Touch browsers synthesise mouseover/mouseenter after a tap, which would
       re-open the bubble the same tap just toggled shut. Once this icon has
       seen a non-mouse pointer, tap is the only thing that drives it. */
    const isTouchTriggered = useRef(false);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        const bubble = bubbleRef.current;
        if (!trigger || !bubble) return;

        const icon = trigger.getBoundingClientRect();
        const { width, height } = bubble.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;

        const fitsAbove = icon.top - height - OFFSET >= GUTTER;
        const fitsBelow = icon.bottom + height + OFFSET <= viewportHeight - GUTTER;
        let side: Placement = placement;
        if (side === 'top' && !fitsAbove && fitsBelow) side = 'bottom';
        else if (side === 'bottom' && !fitsBelow && fitsAbove) side = 'top';

        const top = side === 'top' ? icon.top - height - OFFSET : icon.bottom + OFFSET;

        const iconCenter = icon.left + icon.width / 2;
        const maxLeft = Math.max(GUTTER, viewportWidth - width - GUTTER);
        const left = Math.min(Math.max(iconCenter - width / 2, GUTTER), maxLeft);

        // Arrow follows the icon within the (possibly shifted) bubble.
        const arrowLeft = Math.min(
            Math.max(iconCenter - left, ARROW_INSET),
            Math.max(width - ARROW_INSET, ARROW_INSET)
        );

        setPosition({ top, left, arrowLeft, placement: side });
    }, [placement]);

    /* Measure before paint, so the bubble never shows at its pre-positioned
       coordinates — it stays transparent until `position` exists. */
    useLayoutEffect(() => {
        if (!isOpen) {
            setPosition(null);
            return;
        }
        updatePosition();
    }, [isOpen, text, updatePosition]);

    useEffect(() => {
        if (!isOpen) return;

        const close = () => setIsOpen(false);
        // Tapping / clicking anywhere else dismisses it (touch has no hover-out).
        const handlePointerDown = (event: PointerEvent) => {
            if (!triggerRef.current?.contains(event.target as Node)) close();
        };
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') close();
        };

        window.addEventListener('resize', updatePosition);
        window.addEventListener('scroll', updatePosition, true);
        document.addEventListener('pointerdown', handlePointerDown, true);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
            document.removeEventListener('pointerdown', handlePointerDown, true);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, updatePosition]);

    return (
        <span
            ref={triggerRef}
            className="ec-hint"
            tabIndex={0}
            role="button"
            aria-label={text}
            onMouseEnter={() => { if (!isTouchTriggered.current) setIsOpen(true); }}
            onMouseLeave={() => { if (!isTouchTriggered.current) setIsOpen(false); }}
            onFocus={() => setIsOpen(true)}
            onBlur={() => setIsOpen(false)}
            /* Touch / pen taps toggle; mouse is already covered by hover. */
            onPointerDown={(event) => {
                if (event.pointerType === 'mouse') return;
                isTouchTriggered.current = true;
                setIsOpen((open) => !open);
            }}
            /* Several hints sit inside a <label>, where a click would
               otherwise focus the field and pop its datepicker open. */
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
            }}
        >
            <span className="ec-hint__icon" aria-hidden="true">ⓘ</span>
            {isOpen && ReactDOM.createPortal(
                <span
                    ref={bubbleRef}
                    role="tooltip"
                    className={`ec-hint__bubble ec-hint__bubble--${position?.placement ?? placement}${position ? ' ec-hint__bubble--visible' : ''}`}
                    style={position
                        ? {
                            top: position.top,
                            left: position.left,
                            ['--ec-hint-arrow-left' as string]: `${position.arrowLeft}px`,
                        } as React.CSSProperties
                        : undefined}
                >
                    {text}
                </span>,
                document.body
            )}
        </span>
    );
};
